import {
  createContext, useContext, useCallback, useState,
  useEffect, useMemo, type ReactNode,
} from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react'
import type { Toast, ToastType } from '@/types'

// ── Types ─────────────────────────────────────────────────────────────────────

interface ToastContextValue {
  toasts:  Toast[]
  toast:   (message: string, type?: ToastType, duration?: number) => void
  dismiss: (id: string) => void
}

// ── Constants ─────────────────────────────────────────────────────────────────

const MAX_TOASTS       = 3      // Rule 3 — max 3 visible at once
const DEFAULT_DURATION = 4000   // ms

// ── Context ───────────────────────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue | null>(null)

// ── Provider ──────────────────────────────────────────────────────────────────

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  const toast = useCallback((
    message:  string,
    type:     ToastType = 'info',
    duration: number    = DEFAULT_DURATION
  ) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`
    setToasts(prev => {
      // Rule 3 — cap at MAX_TOASTS, drop oldest on overflow
      const next = [...prev, { id, message, type, duration }]
      return next.length > MAX_TOASTS ? next.slice(next.length - MAX_TOASTS) : next
    })
  }, [])

  // Auto-dismiss each toast after its duration
  useEffect(() => {
    if (!toasts.length) return
    const timers = toasts.map(t => setTimeout(() => dismiss(t.id), t.duration))
    return () => timers.forEach(clearTimeout)
  }, [toasts, dismiss])

  const value = useMemo<ToastContextValue>(
    () => ({ toasts, toast, dismiss }),
    [toasts, toast, dismiss]
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastRegion toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

// ── Toast Region UI ───────────────────────────────────────────────────────────

const TOAST_ICONS: Record<ToastType, typeof CheckCircle2> = {
  success: CheckCircle2,
  error:   XCircle,
  warning: AlertTriangle,
  info:    Info,
}

// Semantic tokens auto-flip for dark mode via CSS variables in index.css.
// Light: pale tinted bg + solid border + dark text.
// Dark:  deep tinted bg + bright border + light text  (all via *-light / *-dark token inversions).
const TOAST_STYLES: Record<ToastType, string> = {
  success: 'bg-success-light border-success text-success-dark',
  error:   'bg-error-light   border-error   text-error-dark',
  warning: 'bg-accent-light  border-accent  text-accent-dark',
  info:    'bg-primary-light border-primary text-primary-dark',
}

function ToastRegion({
  toasts,
  onDismiss,
}: {
  toasts:    Toast[]
  onDismiss: (id: string) => void
}) {
  return (
    <div
      role="region"
      aria-live="polite"
      aria-label="Notifications"
      className="fixed bottom-24 left-4 right-4 z-50 flex flex-col gap-2 pointer-events-none"
    >
      <AnimatePresence mode="popLayout">
        {toasts.map(t => {
          const Icon = TOAST_ICONS[t.type]
          return (
            <motion.div
              key={t.id}
              role="status"
              aria-atomic="true"
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0,  scale: 1    }}
              exit={{    opacity: 0, y: -8, scale: 0.95 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className={`pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-xl border shadow-md text-sm font-medium ${TOAST_STYLES[t.type]}`}
            >
              <Icon size={18} aria-hidden="true" />
              <span className="flex-1">{t.message}</span>
              <button
                onClick={() => onDismiss(t.id)}
                aria-label="Dismiss notification"
                className="ml-auto -mr-1 p-1 rounded-lg opacity-60 hover:opacity-100 transition-opacity
                           min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <X size={14} aria-hidden="true" />
              </button>
            </motion.div>
          )
        })}
      </AnimatePresence>
    </div>
  )
}

// ── Hook (Rule 7 — throws outside ToastProvider) ──────────────────────────────

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error(
    '[Nudge] useToast must be used inside <ToastProvider>. ' +
    'Wrap your app root — see state-managementSKILL.md.'
  )
  return ctx
}
